/**
 * Assertion helpers shared by every drill harness.
 *
 * Prepended to a drill's own fixtures and cases. Written as a string because it
 * is compiled together with the submitted code rather than imported, and as
 * ES5-flavoured JavaScript because it is concatenated into the compiled output.
 *
 * Everything is `__`-prefixed: this shares a scope with the submitted code, and
 * a candidate renaming a variable must not be able to break the checker.
 *
 * The one rule these encode: every call goes through a wrapper that catches, so
 * a failure can always report what was called. Calling directly and asserting
 * afterwards lets a thrown error escape stripped of context, which produces the
 * useless "TypeError: x is not a function" with no indication of which case.
 *
 * `__gen` reads `__seed`, which the runner declares above this. It is the number
 * of times the drill has been attempted, so generated inputs are stable while
 * you iterate on one attempt and different the next time the drill comes round.
 */
export const HARNESS_KIT = `
function __show(v) {
  if (typeof v === "function") return "[function]";
  if (v === undefined) return "undefined";
  try { return JSON.stringify(v); } catch (e) { return String(v); }
}

function __fail(call, lines) {
  throw new Error(call + "\\n" + lines.join("\\n"));
}

function __attempt(fn) {
  try { return { ok: true, value: fn() }; }
  catch (e) { return { ok: false, error: (e && e.message) ? String(e.message) : String(e) }; }
}

async function __attemptAsync(fn) {
  try { return { ok: true, value: await fn() }; }
  catch (e) { return { ok: false, error: (e && e.message) ? String(e.message) : String(e) }; }
}

// Structural equality for expected values. Deliberately strict about NaN and
// about array-vs-object, because several drills are about exactly that.
function __eq(a, b) {
  if (a === b) return true;
  if (typeof a === "number" && typeof b === "number") return Number.isNaN(a) && Number.isNaN(b);
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!__eq(a[k], b[k])) return false;
  }
  return true;
}

function __expectExact(call, fn, expected) {
  const r = __attempt(fn);
  if (!r.ok) __fail(call, ["expected " + __show(expected), "but it threw: " + r.error]);
  if (!__eq(r.value, expected)) __fail(call, ["expected " + __show(expected), "but got " + __show(r.value)]);
}

async function __expectExactAsync(call, fn, expected) {
  const r = await __attemptAsync(fn);
  if (!r.ok) __fail(call, ["expected " + __show(expected), "but it rejected: " + r.error]);
  if (!__eq(r.value, expected)) __fail(call, ["expected " + __show(expected), "but got " + __show(r.value)]);
}

function __expectThrows(call, fn, why) {
  const r = __attempt(fn);
  if (r.ok) __fail(call, ["expected it to throw, because " + why, "but it returned " + __show(r.value)]);
}

async function __expectRejects(call, fn, why) {
  const r = await __attemptAsync(fn);
  if (r.ok) __fail(call, ["expected it to reject, because " + why, "but it resolved with " + __show(r.value)]);
}

function __assert(call, condition, lines) {
  if (!condition) __fail(call, lines);
}

// ---------------------------------------------------------------------------
// Generated inputs.
//
// A drill whose cases are fixed literals stops testing anything once you have
// seen it three times: spaced repetition serves the same problem next week, and
// remembering the answers is not the skill being drilled. These build the inputs
// instead, from a seed the runner derives from how many times you have attempted
// this drill. Different every rep, identical across the runs inside one attempt,
// and reproducible from the seed alone when a case needs explaining.
// ---------------------------------------------------------------------------

// splitmix32. Small, fast, and good enough that generated arrays do not have
// visible structure. Seeded rather than Math.random: an input you cannot
// reproduce is an input you cannot debug.
function __rng(seed) {
  let s = (seed >>> 0) + 0x9e3779b9;
  return function () {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return ((z ^ (z >>> 15)) >>> 0) / 4294967296;
  };
}

function __hash(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 16777619) >>> 0;
  }
  return h >>> 0;
}

/**
 * A generator for one case.
 *
 * The salt is mixed with the attempt seed so each case draws its own stream.
 * Without it, adding a case shifts the inputs of every case after it, and a
 * failure you saw yesterday is not the failure you see today.
 */
function __gen(salt) {
  const base = (typeof __seed === "number" ? __seed : 0) >>> 0;
  const next = __rng((Math.imul(base + 1, 2654435761) ^ __hash(String(salt))) >>> 0);
  const g = {
    next: next,
    int: function (min, max) {
      if (max < min) return min;
      return min + Math.floor(next() * (max - min + 1));
    },
    float: function (min, max, precision) {
      const raw = min + next() * (max - min);
      const p = Math.pow(10, precision === undefined ? 0 : precision);
      return Math.round(raw * p) / p;
    },
    bool: function () { return next() < 0.5; },
    pick: function (list) { return list[g.int(0, list.length - 1)]; },
    str: function (len, alphabet) {
      let out = "";
      for (let i = 0; i < len; i++) out += g.pick(alphabet);
      return out;
    },
    shuffle: function (list) {
      const copy = list.slice();
      for (let i = copy.length - 1; i > 0; i--) {
        const j = g.int(0, i);
        const t = copy[i]; copy[i] = copy[j]; copy[j] = t;
      }
      return copy;
    },
    /**
     * @param len how many elements
     * @param of  called per element, returns the value
     * @param opts { distinct, sorted } - distinct retries a clash rather than
     *             looping forever, so a too-small element space degrades to a
     *             shorter array instead of hanging the worker.
     */
    array: function (len, of, opts) {
      const options = opts || {};
      const out = [];
      const seen = new Set();
      let guard = 0;
      while (out.length < len && guard < len * 20 + 50) {
        guard++;
        const value = of(out.length);
        if (options.distinct) {
          const key = __show(value);
          if (seen.has(key)) continue;
          seen.add(key);
        }
        out.push(value);
      }
      if (options.sorted) {
        out.sort(function (a, b) {
          if (typeof a === "number" && typeof b === "number") return a - b;
          return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
        });
      }
      return out;
    }
  };
  return g;
}

// ---------------------------------------------------------------------------
// Judges that are not "match this one literal".
//
// Several drills have more than one correct answer, or an answer too large to
// write down. Asserting one blessed output there teaches the shape of the
// reference solution rather than the rules of the problem.
// ---------------------------------------------------------------------------

// Canonical form for order-free comparison: every array sorted by its rendered
// contents, recursively. Only for outputs whose order genuinely is free.
function __canonical(v) {
  if (Array.isArray(v)) {
    const copy = v.map(__canonical);
    copy.sort(function (a, b) {
      const A = __show(a), B = __show(b);
      return A < B ? -1 : A > B ? 1 : 0;
    });
    return copy;
  }
  return v;
}

function __expectSameSet(call, fn, expected) {
  const r = __attempt(fn);
  if (!r.ok) __fail(call, ["expected the same entries as " + __show(expected), "but it threw: " + r.error]);
  if (!__eq(__canonical(r.value), __canonical(expected))) {
    __fail(call, ["expected the same entries as " + __show(expected) + ", in any order", "but got " + __show(r.value)]);
  }
}

/**
 * Check the properties the problem states, rather than one accepted output.
 *
 * The check returns true, or a sentence saying which rule was broken. The sentence
 * is what the candidate reads, so it names the rule and not the mismatch.
 */
function __expectProperty(call, fn, check) {
  const r = __attempt(fn);
  if (!r.ok) __fail(call, ["expected a valid result", "but it threw: " + r.error]);
  const verdict = check(r.value);
  if (verdict !== true) {
    __fail(call, [typeof verdict === "string" ? verdict : "the result broke one of the stated rules",
                  "got " + __show(r.value)]);
  }
}

/**
 * Compare against an answer computed a second, obviously-correct way.
 *
 * The oracle is written for clarity rather than speed, which is exactly what the
 * drill forbids, so it can be trusted about what the answer is while the drill
 * is still about how you get there. Generated inputs need this: nobody can write
 * the expected output for a stream of two hundred random keys.
 */
function __expectOracle(call, fn, oracle) {
  const expected = oracle();
  const r = __attempt(fn);
  if (!r.ok) __fail(call, ["expected " + __show(expected), "but it threw: " + r.error]);
  if (!__eq(r.value, expected)) {
    __fail(call, ["expected " + __show(expected), "but got " + __show(r.value)]);
  }
}

// Lets queued microtasks run, so pending .then callbacks have fired.
function __tick() { return Promise.resolve().then().then().then().then(); }

// Runs a promise to completion against the fake clock, stepping time forward to
// each scheduled timer in turn. Without this, anything that awaits a sleep just
// hangs: the fake clock never moves on its own, and the harness cannot know the
// backoff schedule in advance. Only usable in drills whose prelude installs the
// clock.
async function __drain(promise) {
  let done = false;
  const settled = promise.then(
    function (v) { done = true; return { ok: true, value: v }; },
    function (e) { done = true; return { ok: false, error: (e && e.message) ? String(e.message) : String(e) }; }
  );
  for (let i = 0; i < 500 && !done; i++) {
    await __tick();
    if (done) break;
    const at = __clock.nextAt();
    if (at === null) break;
    __clock.advance(Math.max(0, at - __clock.now));
  }
  await __tick();
  return settled;
}
`;
